import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/apiService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Define public routes that don't need authentication
const publicRoutes = [
  '/company-user-login',
  '/public-vendor-registration-form',
  '/public-vendor-acknowledgement',
  '/public-document-request',
  '/vendor-documents',
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimeoutRef = useRef(null);

  /**
   * Verify session by calling /auth/me
   * Cookies are sent automatically with withCredentials: true
   */
  const verifySession = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        // Schedule token refresh (14 minutes before expiry for 15-min tokens)
        scheduleTokenRefresh(14 * 60 * 1000);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session verification failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  /**
   * Schedule automatic token refresh
   */
  const scheduleTokenRefresh = useCallback((delay) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    refreshTimeoutRef.current = setTimeout(async () => {
      try {
        await authService.refresh();
        // Reschedule for next refresh
        scheduleTokenRefresh(14 * 60 * 1000);
      } catch (error) {
        console.error('Token refresh failed:', error);
        // If refresh fails, session is invalid
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/company-user-login';
      }
    }, delay);
  }, []);

  /**
   * Initialize auth state on app load
   */
  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check for public routes
      const currentPath = window.location.pathname;
      if (publicRoutes.some(route => currentPath.startsWith(route))) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      await verifySession();
      setLoading(false);
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [verifySession]);

  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });
      const data = response?.data || {};

      // (a) normal session
      if (response.success && data.user) {
        setUser(data.user);
        setIsAuthenticated(true);
        scheduleTokenRefresh(14 * 60 * 1000);
        return { success: true, status: 'session' };
      }
      // (b) super-admin first-login bootstrap — password reset required
      if (response.success && data.setup_required) {
        return { success: false, status: 'setup_required', setupToken: data.setup_token };
      }
      // (c) super-admin has 2FA enabled — a TOTP code is required next
      if (response.success && data.twofa_required) {
        return { success: false, status: 'twofa_required', pendingToken: data.pending_token };
      }

      return {
        success: false,
        status: 'error',
        error: 'Login failed: Invalid response from server',
      };
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle rate limit errors specifically
      if (error.response?.status === 429) {
        const resetTime = error.response?.headers?.['x-ratelimit-reset'];
        const remaining = error.response?.headers?.['x-ratelimit-remaining'];
        
        let message = 'Too many login attempts. Please wait a few minutes before trying again.';
        
        if (resetTime) {
          const resetDate = new Date(parseInt(resetTime) * 1000);
          const minutes = Math.ceil((resetDate.getTime() - Date.now()) / 60000);
          if (minutes > 0) {
            message = `Too many login attempts. Please try again in ${minutes} minute(s).`;
          }
        }
        
        return { success: false, status: 'error', error: message };
      }

      const message = error.response?.data?.error?.message
        || error.response?.data?.detail
        || error.message
        || 'Login failed. Please check your credentials and try again.';
      return { success: false, status: 'error', error: message };
    }
  };

  /**
   * Complete super-admin login when 2FA is enabled: exchange the pending challenge + a TOTP code
   * for a real session.
   */
  const verifyLogin2fa = async (pendingToken, code) => {
    try {
      const response = await authService.verifyLogin2fa({ pending_token: pendingToken, code });
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        scheduleTokenRefresh(14 * 60 * 1000);
        return { success: true };
      }
      return { success: false, error: 'Verification failed. Please try again.' };
    } catch (error) {
      const message =
        error.response?.data?.error?.message ||
        error.message ||
        'Invalid or expired code. Please try again.';
      return { success: false, error: message };
    }
  };

  /**
   * Logout - clear session and cookies
   */
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      // Clear local state
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear any scheduled refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Redirect to login
      window.location.href = '/company-user-login';
    }
  };

  /**
   * Logout from all devices
   */
  const logoutAll = async () => {
    try {
      await authService.logoutAll();
    } catch (error) {
      console.warn('Logout all error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      window.location.href = '/company-user-login';
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verifyLogin2fa,
    logout,
    logoutAll,
    refreshUser,
    hasRole,
    hasAnyRole,
    // Computed role checks for convenience
    isAdmin: user?.role === 'ADMIN',
    isCoordinator: user?.role === 'COORDINATOR',
    isUser: user?.role === 'USER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
